import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import MessageBubble from './MessageBubble';
import ChartRenderer from './ChartRenderer';

// ─── CONFIG ──────────────────────────────────────────────────────────────────
// SQL Agent API (localhost:8010) — requires SSH port-forward in production
const API_BASE = process.env.REACT_APP_API_BASE ?? 'http://localhost:8010';

// Streaming: target duration to type out any response (ms)
const STREAM_DURATION_MS = 2800;
const TICK_MS = 16; // ~60fps

// ─── Context-aware loading phase messages ─────────────────────────────────
const LOADING_PHASES = {
  default: [
    'Thinking…',
    'Searching SAP B1 data…',
    'Analyzing your query…',
    'Generating response…',
  ],
  invoice: [
    'Analyzing invoice data…',
    'Checking payment records…',
    'Calculating overdue amounts…',
    'Generating collection report…',
  ],
  inventory: [
    'Scanning inventory levels…',
    'Checking reorder points…',
    'Evaluating stock risk…',
    'Preparing stock report…',
  ],
  sales: [
    'Pulling sales records…',
    'Analyzing order history…',
    'Calculating revenue metrics…',
    'Generating sales report…',
  ],
  credit: [
    'Validating credit limit…',
    'Checking order value…',
    'Reviewing customer account…',
    'Generating validation result…',
  ],
  email: [
    'Drafting email content…',
    'Fetching recipient details…',
    'Personalizing message…',
    'Preparing email template…',
  ],
};

/** Pick the right phase set based on what the user asked */
const getLoadingPhases = (query) => {
  const q = (query ?? '').toLowerCase();
  if (/email|\bmail\b|notify|notification|reminder|dunning letter/.test(q))
    return LOADING_PHASES.email;
  if (/overdue|invoice|receivable|payment|dunning|\bar\b/.test(q))
    return LOADING_PHASES.invoice;
  if (/stock|inventory|reorder|warehouse|item/.test(q))
    return LOADING_PHASES.inventory;
  if (/sales|revenue|order|\bbp\b/.test(q))
    return LOADING_PHASES.sales;
  if (/credit|validate|limit|approv/.test(q))
    return LOADING_PHASES.credit;
  return LOADING_PHASES.default;
};

/**
 * normalizeQuery — backend expects a plain string.
 */
const normalizeQuery = (query) => {
  if (Array.isArray(query)) return query.map(q => q.trim()).filter(Boolean).join('\n');
  return (query ?? '').trim();
};

/**
 * StreamingTable — renders a { rows: [...] } table during streaming.
 * Mobile-first: horizontal scroll, touch-friendly, smart column sizing.
 */
const formatHeader = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const isCompactCol = (col) =>
  /code|invoice|customer|amount|on_hand|reorder|days|date|qty|price|id|no$|num/i.test(col);

const isNumericCol = (col) =>
  /amount|on_hand|reorder|days|qty|price|count|total/i.test(col);

/** Short alphanumeric code columns — wider than numbers, narrower than descriptions */
const isCodeCol = (col) =>
  /_code$|_id$|_no$|_num$|_ref$|_key$|^code$|^sku$|^part/i.test(col);

/** Mobile header abbreviations — matches MessageBubble.js */
const MOBILE_ABBREV = {
  reorder_point: 'Reorder', invoice_number: 'Invoice #', customer_code: 'Customer',
  customer_name: 'Customer', item_code: 'Code', item_name: 'Item',
  days_overdue: 'Days', total_overdue: 'Total', overdue_amount: 'Amount',
  credit_limit: 'Limit', current_balance: 'Balance', sales_rep: 'Rep',
  lead_time_days: 'Lead', lost_sales: 'Lost Sales', invoice_count: 'Invoices',
};
const formatMobileHeader = (key) => {
  if (MOBILE_ABBREV[key]) return MOBILE_ABBREV[key];
  const full = formatHeader(key);
  if (full.length > 10) return full.split(' ')[0];
  return full;
};

const StreamingTable = ({ table }) => {
  if (!table?.rows?.length) return null;
  const cols = Object.keys(table.rows[0]);

  const cellValue = (col, row) => {
    const isLow = col === 'on_hand' && row.reorder_point != null && Number(row[col]) < Number(row.reorder_point);
    if (isLow) return (
      <span style={{ color: '#ef4444', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
        <i className="fas fa-triangle-exclamation" style={{ fontSize: '10px' }} />
        {row[col]}
      </span>
    );
    return <span style={{ color: 'var(--text-secondary)' }}>{String(row[col])}</span>;
  };

  return (
    <div className="mb-3">

      {/* ── MOBILE: same smart table as completed MessageBubble ── */}
      {(() => {
        const TEXT_W = 2.5, CODE_W = 1.5, NUM_W = 1.0;
        const colW   = (col) => isNumericCol(col) ? NUM_W : isCodeCol(col) ? CODE_W : TEXT_W;
        const totalW = cols.reduce((s, c) => s + colW(c), 0);
        const colPct = (col) => `${(colW(col) / totalW * 100).toFixed(1)}%`;
        const dynFont = cols.length <= 4 ? '12px' : cols.length <= 6 ? '11px' : '10px';
        return (
          <div className="md:hidden">
            <div className="rounded-xl border" style={{ borderColor: 'var(--glass-border)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' }}>
                <colgroup>
                  {cols.map(col => <col key={col} style={{ width: colPct(col) }} />)}
                </colgroup>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: 'var(--accent-light)' }}>
                    {cols.map((col, ci) => (
                      <th key={col} title={formatHeader(col)} style={{
                        padding: '6px 4px', fontSize: '9px', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0, color: '#6366f1',
                        textAlign: isNumericCol(col) ? 'right' : 'left',
                        borderBottom: '2px solid var(--glass-border)',
                        borderRight: ci < cols.length - 1 ? '1px solid var(--glass-border)' : 'none',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        background: 'var(--accent-light)',
                      }}>
                        {formatMobileHeader(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, i) => {
                    const isLow = cols.some(c => c === 'on_hand' && row.reorder_point != null && Number(row[c]) < Number(row.reorder_point));
                    return (
                      <tr key={i} style={{
                        background: i % 2 === 0 ? 'var(--bg-glass)' : 'transparent',
                        borderLeft: `2px solid ${isLow ? '#ef4444' : 'transparent'}`,
                      }}>
                        {cols.map((col, ci) => {
                          const isText = !isNumericCol(col) && !isCodeCol(col);
                          const isLowCell = col === 'on_hand' && row.reorder_point != null && Number(row[col]) < Number(row.reorder_point);
                          return (
                            <td key={col} title={String(row[col] ?? '')} style={{
                              padding: '5px 4px', fontSize: dynFont, fontWeight: 500,
                              textAlign: isNumericCol(col) ? 'right' : 'left',
                              verticalAlign: 'top',
                              color: isLowCell ? '#ef4444' : 'var(--text-secondary)',
                              borderTop: '1px solid var(--glass-border)',
                              borderRight: ci < cols.length - 1 ? '1px solid var(--glass-border)' : 'none',
                              fontVariantNumeric: isNumericCol(col) ? 'tabular-nums' : 'normal',
                              ...(isText ? {
                                overflow: 'hidden', display: '-webkit-box',
                                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                whiteSpace: 'normal', wordBreak: 'break-word',
                              } : {
                                whiteSpace: 'nowrap', overflow: 'hidden',
                                textOverflow: 'ellipsis', maxWidth: 0,
                              }),
                            }}>
                              {String(row[col] ?? '')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── DESKTOP: traditional table ── */}
      <div className="hidden md:block rounded-xl border overflow-x-auto" style={{ borderColor: 'var(--glass-border)' }}>
        <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
          <thead>
            <tr style={{ background: 'var(--accent-light)' }}>
              {cols.map((col, ci) => (
                <th key={col} style={{
                  padding: '9px 12px', whiteSpace: 'nowrap',
                  textAlign: isNumericCol(col) ? 'right' : 'left',
                  fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: '#6366f1',
                  borderBottom: '2px solid var(--glass-border)',
                  borderRight: ci < cols.length - 1 ? '1px solid var(--glass-border)' : 'none',
                }}>
                  {formatHeader(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? 'var(--bg-glass)' : 'transparent' }}>
                {cols.map((col, ci) => (
                  <td key={col} style={{
                    padding: '7px 12px', fontSize: '12px',
                    whiteSpace: isCompactCol(col) ? 'nowrap' : 'normal',
                    maxWidth: isCompactCol(col) ? 'none' : '200px',
                    textAlign: isNumericCol(col) ? 'right' : 'left',
                    verticalAlign: 'top',
                    borderTop: '1px solid var(--glass-border)',
                    borderRight: ci < cols.length - 1 ? '1px solid var(--glass-border)' : 'none',
                  }}>
                    {cellValue(col, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────


// ─── Time-based greeting (no emoji) ───────────────────────────────────────────
// WAV encoding helpers for voice recording (matches backend Streamlit reference)
// Records 16 kHz mono PCM via Web Audio API, encodes as 16-bit WAV / audio/wav.

const mergeFloat32 = (chunks) => {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out   = new Float32Array(total);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
};

const encodeWAV = (samples, sampleRate) => {
  const buf  = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buf);
  const str  = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true);
  str(8, 'WAVE'); str(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  str(36, 'data'); view.setUint32(40, samples.length * 2, true);
  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }
  return new Blob([buf], { type: 'audio/wav' });
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Welcome back';
};

const ChatArea = ({ ragEnabled, messages, setMessages, onQueryComplete, theme, newConversation }) => {
  const [input, setInput]             = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showThinking, setShowThinking] = useState(true);
  const [isLoading, setIsLoading]     = useState(false);
  const [sendRipple, setSendRipple]   = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // ── Streaming states ──────────────────────────────────────────────────────
  const [streamingMsg, setStreamingMsg] = useState(null);
  const [loadingPhase, setLoadingPhase] = useState(LOADING_PHASES.default[0]);
  const [phaseKey, setPhaseKey]         = useState(0);
  // Holds the active phase set for the current query (set before API call starts)
  const loadingPhasesRef = useRef(LOADING_PHASES.default);

  const messagesEndRef      = useRef(null);
  const textareaRef          = useRef(null);
  const streamTimerRef       = useRef(null);
  const audioContextRef      = useRef(null);  // Web Audio API context (WAV recording)
  const scriptProcessorRef   = useRef(null);  // ScriptProcessorNode
  const pcmChunksRef         = useRef([]);    // captured Float32 PCM chunks
  const stopVoiceRef         = useRef(null);  // stop-and-send callback
  const mediaStreamRef       = useRef(null);  // mic stream (for cleanup)
  const chatScrollRef        = useRef(null);
  const silenceTimerRef      = useRef(null);

  // Logo source — theme-aware
  const logoSrc = theme === 'dark'
    ? '/averina-ai-app-icon.png'
    : '/averina-ai-icon.png';

  // ── Auto-resize textarea ──────────────────────────────────────────────────
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  // ── Auto-scroll on new messages or streaming update ───────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMsg]);

  // ── Cycle loading phase text while API call is in flight ────────�  // ── Voice recording: Web Audio API → WAV → /v1/query/voice (Sarvam AI STT) ──────
  // Matches backend reference (streamlit_test_client.py): recording.wav / audio/wav.
  // Records 16 kHz mono PCM via ScriptProcessorNode, encodes to WAV on stop.
  const toggleVoice = async () => {
    // ── Stop recording ──
    if (isRecording) {
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
      if (stopVoiceRef.current)    { stopVoiceRef.current(); stopVoiceRef.current = null; }
      setIsRecording(false);
      return;
    }

    // ── Start recording ──
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } });
    } catch {
      alert('Microphone access denied. Please allow microphone permission and try again.');
      return;
    }
    mediaStreamRef.current = stream;
    pcmChunksRef.current   = [];

    // Capture history NOW (before recording — avoids stale closure issue)
    const historyAtStart = [];
    for (const m of messages) {
      // Skip error messages — UI feedback only, not valid context for backend
      if (m.isError) continue;
      // Skip voice placeholders that never got a transcript (Sarvam returned nothing)
      if (m.isVoice && m.content === 'Listening...') continue;
      if (m.role === 'user')           historyAtStart.push({ role: 'user',   content: m.content });
      else if (m.role === 'assistant') historyAtStart.push({ role: 'system', content: m.content });
    }

    // ── Web Audio API: capture 16 kHz mono PCM ──────────────────────────
    const audioCtx  = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
    const source    = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    audioContextRef.current    = audioCtx;
    scriptProcessorRef.current = processor;

    processor.onaudioprocess = (e) => {
      pcmChunksRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
    };
    source.connect(processor);
    processor.connect(audioCtx.destination); // required for onaudioprocess to fire

    // ── Stop-and-send closure ────────────────────────────────────────────
    const doStop = async () => {
      // Disconnect and release all audio resources
      processor.disconnect();
      source.disconnect();
      audioCtx.close();
      audioContextRef.current    = null;
      scriptProcessorRef.current = null;
      stream.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;

      const chunks = pcmChunksRef.current;
      pcmChunksRef.current = [];
      if (!chunks.length) return;

      // Encode as 16-bit mono WAV — matches backend reference (recording.wav / audio/wav)
      const wavBlob = encodeWAV(mergeFloat32(chunks), 16000);
      if (wavBlob.size < 200) return; // too short to be meaningful

      // Placeholder user bubble — replaced with real transcript after API responds
      const voicePlaceholder = {
        role: 'user', content: 'Listening...', isVoice: true, timestamp: Date.now(),
      };
      setMessages(prev => [...prev, voicePlaceholder]);
      loadingPhasesRef.current = LOADING_PHASES.default;
      setLoadingPhase(LOADING_PHASES.default[0]);
      setIsLoading(true);

      try {
        const formData = new FormData();
        // Send as WAV — matches backend reference: ("recording.wav", audio_bytes, "audio/wav")
        formData.append('audio', wavBlob, 'recording.wav');
        if (historyAtStart.length > 0) {
          formData.append('messages', JSON.stringify(historyAtStart));
        }

        const res = await axios.post(`${API_BASE}/v1/query/voice`, formData, {
          withCredentials: false,
          // Do NOT set Content-Type — browser sets correct multipart boundary automatically
        });

        const data = res.data;

        // Update placeholder with actual Sarvam AI transcript
        if (data.transcript) {
          setMessages(prev =>
            prev.map(m => m === voicePlaceholder ? { ...m, content: data.transcript } : m)
          );
        }

        // Same response handling as text queries
        if (data.clarification_needed) {
          setIsLoading(false);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: data.clarification_question ?? 'Could you provide more details?',
            isClarification: true, timestamp: Date.now(),
          }]);
          return;
        }
        if (data.error) {
          setIsLoading(false);
          setMessages(prev => [...prev, {
            role: 'assistant',
            // Guide §5: show a generic message — data.error may contain internal DB details
            content: 'Something went wrong while processing your query. Please try rephrasing it.',
            isError: true, timestamp: Date.now(),
          }]);
          return;
        }

        const fullContent = data.summary ?? '';
        const tableData   = data.data?.length ? { rows: data.data } : null;
        const msgMeta = {
          role: 'assistant', table: tableData,
          chart: data.chart ?? null, sql: data.sql ?? null,
          question: data.question ?? '', timestamp: Date.now(),
        };
        setIsLoading(false);
        startStreaming(fullContent, msgMeta);

      } catch (err) {
        console.error('Voice API error:', err);
        let errMsg;
        if (err.response) {
          const status = err.response.status;
          if (status === 413)              errMsg = 'Voice recording is too large (limit: 25 MB). Please record a shorter clip.';
          else if (status === 502 || status === 500) errMsg = 'The backend encountered an error. Please try again in a moment.';
          else                             errMsg = err.response.data?.detail ?? `Server error ${status}`;
        } else if (err.message?.includes('Network Error') || err.code === 'ECONNREFUSED') {
          errMsg = 'Cannot reach the SQL Agent API on port 8010. Ensure the backend is running.';
        } else {
          errMsg = err.message ?? 'Voice query failed. Please try again.';
        }
        setIsLoading(false);
        setMessages(prev => [...prev, {
          role: 'assistant', content: errMsg, isError: true, timestamp: Date.now(),
        }]);
      }
    };

    stopVoiceRef.current = doStop;
    setIsRecording(true);

    // Auto-stop after 60 seconds
    silenceTimerRef.current = setTimeout(() => {
      if (stopVoiceRef.current) {
        stopVoiceRef.current();
        stopVoiceRef.current = null;
        setIsRecording(false);
      }
    }, 60000);
  };

  // ── Start character-by-character streaming of a full string ──────────────
  const startStreaming = (fullContent, msgMeta) => {
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);

    const totalTicks  = STREAM_DURATION_MS / TICK_MS;
    const charsPerTick = Math.max(1, Math.ceil(fullContent.length / totalTicks));
    let charIdx = 0;

    // Show the bubble immediately with empty content + cursor
    setStreamingMsg({ content: '', ...msgMeta });

    streamTimerRef.current = setInterval(() => {
      charIdx = Math.min(charIdx + charsPerTick, fullContent.length);
      const partial = fullContent.slice(0, charIdx);

      setStreamingMsg(prev => prev ? { ...prev, content: partial } : null);

      if (charIdx >= fullContent.length) {
        // Streaming done — move to permanent messages, clear streaming bubble
        clearInterval(streamTimerRef.current);
        setMessages(prev => [...prev, { ...msgMeta, content: fullContent }]);
        setStreamingMsg(null);
      }
    }, TICK_MS);
  };

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = async (overrideInput) => {
    const text = normalizeQuery(overrideInput ?? input);
    if (!text && attachments.length === 0) return;

    setSendRipple(true);
    setTimeout(() => setSendRipple(false), 450);

    const userMsg = { role: 'user', content: text, attachments, timestamp: Date.now() };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setAttachments([]);

    // Pick context-aware loading messages BEFORE starting the spinner
    loadingPhasesRef.current = getLoadingPhases(text);
    setLoadingPhase(loadingPhasesRef.current[0]);
    setIsLoading(true);

    const startTime = Date.now();

    // ── Build message history for the new SQL Agent API ──────────────────────
    // Rules:
    //  - "user" role for user messages
    //  - "system" role for prior AI summaries (and clarification questions)
    //  - Skip isError messages — they are UI feedback, not valid backend context
    //  - Skip voice placeholders with no transcript (content still 'Listening...')
    const historyMessages = [];
    for (const m of messages) {
      if (m.isError) continue; // error msgs are UI-only — never send as context
      if (m.role === 'user') {
        if (m.isVoice && m.content === 'Listening...') continue; // stale placeholder
        historyMessages.push({ role: 'user', content: m.content });
      } else if (m.role === 'assistant') {
        // Includes clarification messages — guide §5 says send as "system"
        historyMessages.push({ role: 'system', content: m.content });
      }
    }

    const messagesPayload = [
      ...historyMessages,
      { role: 'user', content: text },
    ];

    try {
      // POST /v1/query — SQL Agent API
      const res = await axios.post(`${API_BASE}/v1/query`, {
        messages: messagesPayload,
      }, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });

      const data = res.data;
      onQueryComplete?.(Date.now() - startTime);

      // ── Handle clarification request ────────────────────────────────────────
      // When the agent can't generate SQL without more info (e.g. missing date range),
      // it returns clarification_needed=true. Show the question; user answers next turn.
      if (data.clarification_needed) {
        setIsLoading(false);
        const clarifyMsg = {
          role: 'assistant',
          content: data.clarification_question ?? 'Could you provide more details?',
          isClarification: true,
          timestamp: Date.now(),
        };
        setMessages(prev => [...prev, clarifyMsg]);
        return;
      }

      // ── Handle SQL/DB-level error ───────────────────────────────────────────
      if (data.error) {
        setIsLoading(false);
        setMessages(prev => [
          ...prev,
          // Guide §5: show generic message — data.error may contain internal DB details
          { role: 'assistant', content: 'Something went wrong while processing your query. Please try rephrasing it.', isError: true, timestamp: Date.now() }
        ]);
        return;
      }


      // ── Map response fields ─────────────────────────────────────────────────
      // summary  → the text to display (also reused as "system" message next turn)
      // data     → raw rows array → convert to {rows} for TableRenderer
      // chart    → chart spec {chart_type, title, x_axis, y_axis} (stored for future rendering)
      const fullContent = data.summary ?? '';
      const tableData   = data.data?.length
        ? { rows: data.data }    // TableRenderer only needs .rows (uses Object.keys(rows[0]) for headers)
        : null;

      const msgMeta = {
        role:      'assistant',
        table:     tableData,
        chart:     data.chart    ?? null,   // { chart_type, title, x_axis, y_axis }
        sql:       data.sql      ?? null,   // generated SQL (for debug/transparency)
        question:  data.question ?? text,   // the resolved question sent to SQL gen
        timestamp: Date.now(),
      };

      // Stop loading spinner — table appears immediately, text streams in
      setIsLoading(false);
      startStreaming(fullContent, msgMeta);

    } catch (err) {
      console.error('API error:', err);
      let errMsg;
      if (err.response) {
        const status = err.response.status;
        if (status === 400) {
          // Bad payload — safe to show the detail (it describes what was wrong with the request)
          errMsg = `Bad request: ${err.response.data?.detail ?? 'check your input and try again'}`;
        } else if (status === 413) {
          // Voice only: audio file over 25 MB (guide §6)
          errMsg = 'Voice recording is too large (limit: 25 MB). Please record a shorter clip.';
        } else if (status === 422) {
          // Pydantic validation failure — role not user/system, or messages array empty (guide §6)
          errMsg = 'Invalid request format. Please try again.';
        } else if (status === 502 || status === 500) {
          // Downstream failure — safe to retry (guide §6)
          errMsg = 'The backend encountered an error. Please try again in a moment.';
        } else {
          errMsg = err.response.data?.detail ?? `Server error ${status}`;
        }
      } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        errMsg = 'Cannot reach the SQL Agent API on port 8010. Ensure the backend is running and port is forwarded.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errMsg = 'Request timed out. The query may be complex — please try again.';
      } else {
        errMsg = err.message ?? 'Unexpected error. Please try again.';
      }
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: errMsg, isError: true, timestamp: Date.now() }
      ]);
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    // Enter = send
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
      return;
    }
    // Up arrow — recall last user message when input is empty
    if (e.key === 'ArrowUp' && !input) {
      e.preventDefault();
      const lastUser = [...messages].reverse().find(m => m.role === 'user');
      if (lastUser) setInput(lastUser.content);
      return;
    }
    // Esc — clear input
    if (e.key === 'Escape') {
      setInput('');
    }
  };

  // ── Global keyboard shortcuts ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      // Ctrl+K = new conversation
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        newConversation?.();
        return;
      }
      // / = focus input (when not already inside a text field)
      if (e.key === '/' && !['INPUT','TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        textareaRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [newConversation]);

  // ── Export full conversation as text file ──────────────────────────────────
  const exportConversation = () => {
    if (!messages.length) return;
    const lines = messages.map(m => {
      const role = m.role === 'user' ? 'You' : 'Averina AI';
      const time = m.timestamp ? new Date(m.timestamp).toLocaleString() : '';
      let text = `[${role}]  ${time}\n${m.content ?? ''}`;
      if (m.table?.rows?.length) {
        const cols = Object.keys(m.table.rows[0]);
        text += '\n\n' + cols.join(' | ') + '\n'
          + m.table.rows.map(r => cols.map(c => String(r[c] ?? '')).join(' | ')).join('\n');
      }
      return text;
    }).join('\n\n' + '-'.repeat(60) + '\n\n');
    const header = `SAP B1 Agentic AI — Conversation Export\n${new Date().toLocaleString()}\n${'='.repeat(60)}\n\n`;
    const blob = new Blob([header + lines], { type: 'text/plain;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `sap-b1-chat-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAttach = () => {
    setAttachments(prev => [...prev, `invoice_${Date.now()}.csv`]);
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Scroll-to-bottom visibility ─────────────────────────────────────────────
  const handleScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
  };
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  // ── Retry last failed message ──────────────────────────────────────────────
  const handleRetry = () => {
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUser) return;
    setMessages(prev => prev.filter(m => !m.isError));
    sendMessage(lastUser.content);
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">

      {/* ── Message list ── */}
      <div
        className="flex-1 overflow-y-auto relative"
        ref={chatScrollRef}
        onScroll={handleScroll}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
      >

        {/* ════ EMPTY STATE — ChatGPT/Claude-style intro ════ */}
        {messages.length === 0 && !streamingMsg && (
          <div className="min-h-full flex flex-col items-center justify-center px-4 py-5 md:py-12 text-center relative">
            {/* Animated dot-grid background */}
            <div className="intro-dot-grid" />

            <div className="intro-logo mb-3 md:mb-6">
              <img
                src={logoSrc}
                alt="Averina AI"
                className="w-12 h-12 md:w-20 md:h-20 object-contain mx-auto"
              />
            </div>

            <h2 className="intro-title text-xl md:text-3xl font-bold tracking-tight leading-tight">
              <span className="gradient-heading">How can I help with Business One?</span>
            </h2>

            <p className="intro-subtitle text-xs text-gray-400 mt-1.5 max-w-xs md:max-w-sm mx-auto leading-relaxed hidden sm:block">
              Agentic AI for SAP B1 — collections, inventory and credit control,
              enriched with official SAP documentation.
            </p>

            <div className="grid grid-cols-2 gap-2 mt-4 md:mt-8 w-full max-w-sm md:max-w-lg">
              {[
                {
                  q: 'Show total gross profit by customer for the last quarter.',
                  color: 'indigo',
                  delay: 0,
                  svg: (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15" aria-hidden="true">
                      <rect x="2"  y="11" width="4" height="9"  rx="1"/>
                      <rect x="9"  y="7"  width="4" height="13" rx="1"/>
                      <rect x="16" y="3"  width="4" height="17" rx="1"/>
                      <rect x="1"  y="20.5" width="22" height="1.5" rx="0.75"/>
                    </svg>
                  ),
                },
                {
                  q: 'Show the sales trend for the last 12 months.',
                  color: 'violet',
                  delay: 1,
                  svg: (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                      <polyline points="17 6 23 6 23 12"/>
                    </svg>
                  ),
                },
                {
                  q: 'Show total quantity sold by warehouse for the last quarter.',
                  color: 'emerald',
                  delay: 2,
                  svg: (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                  ),
                },
                {
                  q: 'Show monthly gross profit for the last 6 months.',
                  color: 'amber',
                  delay: 3,
                  svg: (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
                      <rect x="3" y="4" width="18" height="18" rx="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8"  y1="2" x2="8"  y2="6"/>
                      <line x1="3"  y1="10" x2="21" y2="10"/>
                      <polyline points="7 16 10 13 13 16 17 12"/>
                    </svg>
                  ),
                },
              ].map(({ q, color, delay, svg }) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="intro-card glass rounded-2xl p-3 text-left hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group"
                  style={{ animationDelay: `${0.55 + delay * 0.08}s` }}
                >
                  <div className={`w-7 h-7 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform`}>
                    {svg}
                  </div>
                  <p className="text-xs font-medium text-gray-700 leading-snug">{q}</p>
                </button>
              ))}
            </div>

          </div>
        )}

        {/* ════ CHAT MESSAGES ════ */}
        {(messages.length > 0 || streamingMsg) && (
          <div className="px-4 md:px-6 pt-6 pb-2">

            {messages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                message={msg}
                showThinking={showThinking}
                theme={theme}
                onRetry={msg.isError ? handleRetry : undefined}
              />
            ))}

            {/* ── Phase 1: Loading — branded spinner while waiting for API ── */}
            {isLoading && (
              <div className="flex items-start gap-3 mb-4 msg-enter">
                {/* Avatar with spinning ring */}
                <div className="ai-avatar-ring flex-shrink-0">
                  <div className="w-8 h-8 rounded-xl overflow-hidden bg-white shadow-md">
                    <img src={logoSrc} alt="AI" className="w-full h-full object-contain" />
                  </div>
                </div>

                {/* Bubble with dots + cycling phase text */}
                <div className="bubble-ai px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-indigo-400 rounded-full dot1"></span>
                    <span className="w-2 h-2 bg-indigo-400 rounded-full dot2"></span>
                    <span className="w-2 h-2 bg-indigo-400 rounded-full dot3"></span>
                  </div>
                  <span
                    key={phaseKey}
                    className="text-xs loading-phase-text"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {loadingPhase}
                  </span>
                </div>
              </div>
            )}

            {/* ── Phase 2: Streaming — table appears instantly, text types out ── */}
            {streamingMsg && (
              <div className="flex items-start gap-3 mb-4 msg-enter">
                <div className="w-8 h-8 rounded-xl overflow-hidden bg-white flex-shrink-0 shadow-md">
                  <img src={logoSrc} alt="AI" className="w-full h-full object-contain" />
                </div>
                <div
                  className="bubble-ai px-4 py-3 rounded-2xl rounded-bl-sm"
                  style={{
                    maxWidth: streamingMsg.table?.rows?.length > 0 ? 'calc(100% - 44px)' : 'min(85%, 680px)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {/* Chart renders immediately — same instant as the table */}
                  {streamingMsg.chart && streamingMsg.table?.rows?.length > 0 && (
                    <ChartRenderer chart={streamingMsg.chart} rows={streamingMsg.table.rows} />
                  )}
                  {/* Table renders immediately — no streaming delay */}
                  {streamingMsg.table?.rows?.length > 0 && (
                    <StreamingTable table={streamingMsg.table} />
                  )}
                  {/* Result text streams in */}
                  {streamingMsg.content && (
                    <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>
                      {streamingMsg.content}
                      <span className="streaming-cursor" aria-hidden="true" />
                    </p>
                  )}
                  {/* Cursor when table exists but text hasn't started yet */}
                  {streamingMsg.table && !streamingMsg.content && (
                    <span className="streaming-cursor" aria-hidden="true" />
                  )}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}

        {messages.length === 0 && !streamingMsg && <div ref={messagesEndRef} />}

        {/* ── Scroll-to-bottom floating button ── */}
        {showScrollBtn && (
          <button
            className="scroll-to-bottom"
            onClick={scrollToBottom}
            title="Jump to latest"
            aria-label="Scroll to bottom"
          >
            <i className="fas fa-arrow-down" />
          </button>
        )}
      </div>

      {/* ── Input bar ── */}
      <div className="p-4">

        {/* Attachment chips */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 px-1">
            {attachments.map((file, i) => (
              <div key={i} className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-1 text-xs text-indigo-700 font-medium">
                <i className="fas fa-file-csv"></i> {file}
                <button onClick={() => removeAttachment(i)} className="text-indigo-300 hover:text-red-400 transition ml-1">
                  <i className="fas fa-xmark"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Main input */}
        <div className="glass rounded-2xl p-2 input-glow border border-white/70 transition-all duration-200">
          <div className="flex items-center gap-2">
            <button onClick={handleAttach} className="hidden sm:flex w-9 h-9 items-center justify-center rounded-xl text-gray-400 hover:bg-indigo-50 hover:text-indigo-500 transition-all" title="Attach file">
              <i className="fas fa-paperclip text-sm" />
            </button>
            <button
              onClick={() => setShowThinking(!showThinking)}
              className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-xl transition-all ${showThinking ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:bg-gray-100'}`}
              title="Toggle reasoning steps"
            >
              <i className="fas fa-brain text-sm" />
            </button>
            {/* Export conversation — only when messages exist */}
            {messages.length > 0 && (
              <button
                onClick={exportConversation}
                className="hidden sm:flex w-9 h-9 items-center justify-center rounded-xl text-gray-400 hover:bg-indigo-50 hover:text-indigo-500 transition-all"
                title="Export conversation as text file"
              >
                <i className="fas fa-download text-sm" />
              </button>
            )}
            <textarea
              ref={textareaRef}
              rows="1"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Averina AI…"
              className="flex-1 bg-transparent outline-none resize-none py-2 text-sm text-gray-700 placeholder-gray-400 overflow-hidden leading-relaxed"
              style={{ maxHeight: '160px' }}
              disabled={isLoading || !!streamingMsg}
            />

            {/* ── Voice input button ── */}
            <button
              onClick={toggleVoice}
              disabled={isLoading || !!streamingMsg}
              className={`voice-btn ${isRecording ? 'recording' : ''} disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none`}
              title={isRecording ? 'Stop recording' : 'Voice input'}
              aria-label={isRecording ? 'Stop voice recording' : 'Start voice input'}
            >
              {/* Ripple rings — visible only while recording */}
              {isRecording && (
                <>
                  <span className="voice-ring" />
                  <span className="voice-ring" />
                  <span className="voice-ring" />
                </>
              )}
              {/* Icon: waveform bars when recording, mic icon when idle */}
              {isRecording ? (
                <span className="voice-wave">
                  <span /><span /><span /><span /><span />
                </span>
              ) : (
                <i className="fas fa-microphone" style={{ position: 'relative', zIndex: 2 }} />
              )}
            </button>

            <button
              onClick={() => sendMessage()}
              disabled={isLoading || !!streamingMsg || !input.trim()}
              className={`relative w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-md hover:shadow-lg hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100 transition-all duration-200 ${sendRipple ? 'send-ripple' : ''} ${input.trim() && !isLoading && !streamingMsg ? 'send-ready' : ''}`}
            >
              <i className="fas fa-arrow-up text-sm" />
            </button>
          </div>
        </div>

        {/* Shortcut hints — desktop only */}
        <p className="hidden sm:block text-center text-[10px] mt-1.5 tracking-wide" style={{ color: 'var(--text-muted)' }}>
          <span className="opacity-70">
            <kbd className="kbd-hint">Shift</kbd>+<kbd className="kbd-hint">Enter</kbd> new line
            &nbsp;&middot;&nbsp;
            <kbd className="kbd-hint">Ctrl</kbd>+<kbd className="kbd-hint">K</kbd> new chat
            &nbsp;&middot;&nbsp;
            <kbd className="kbd-hint">/</kbd> focus input
            &nbsp;&middot;&nbsp;
            <kbd className="kbd-hint">&uarr;</kbd> recall last query
          </span>
        </p>
        <p className="text-center text-[10px] text-gray-400 mt-1 tracking-wide">
          Powered by{' '}
          <a
            href="https://www.averinaai.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold hover:text-indigo-500 transition-colors duration-200"
            style={{ color: 'var(--text-muted)' }}
          >
            Averina AI
          </a>
        </p>
      </div>
    </div>
  );

};

export default ChatArea;
